import React, { useEffect, useRef, useState, useCallback } from 'react';
import api, { fmtNum } from '@/lib/api';
import { streamAiChat } from '@/lib/aiStream';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionTitle } from '@/components/Shared';
import { Sparkles, Send, Trash2, Loader2, CloudSun, Truck, AlertTriangle, RefreshCw, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logError } from '@/lib/log';

const QUICK_PROMPTS = [
  'Who are my top customers this month?',
  'Summarise orders and revenue for the last 7 days',
  'Which orders are pending dispatch right now?',
  'How is my fleet utilisation today?',
];

const AI_MODELS = [
  { key: 'gemini', label: 'Gemini' },
  { key: 'claude', label: 'Claude' },
];

const riskColor = (r) => (r === 'high' ? 'bg-red-100 text-red-700' : r === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700');

export default function AdminAI() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState(() => localStorage.getItem('ai_model') || 'gemini');
  const [pred, setPred] = useState(null);

  const pickModel = (key) => { setModel(key); localStorage.setItem('ai_model', key); };
  const [predBusy, setPredBusy] = useState(false);
  const [waMsgs, setWaMsgs] = useState([]);
  const [waInput, setWaInput] = useState('');
  const [waBusy, setWaBusy] = useState(false);
  const [waPhone] = useState(() => `preview-${Math.random().toString(36).slice(2, 8)}`);
  const waEndRef = useRef(null);
  const endRef = useRef(null);

  const waSend = async (e) => {
    e?.preventDefault();
    const q = waInput.trim();
    if (!q || waBusy) return;
    setWaInput('');
    setWaBusy(true);
    setWaMsgs((m) => [...m, { from: 'customer', text: q }]);
    try {
      const r = await api.post('/whatsapp/simulate', { phone: waPhone, message: q });
      setWaMsgs((m) => [...m, ...r.data.replies.map((t) => ({ from: 'bot', text: t }))]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Simulation failed');
    }
    setWaBusy(false);
  };
  useEffect(() => { waEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [waMsgs]);

  const loadHistory = useCallback(() => {
    api.get('/ai/chat/history', { params: { mode: 'admin_assistant' } })
      .then((r) => setMsgs(r.data.messages.map((m) => ({ sender: m.sender, content: m.content }))))
      .catch((e) => logError('AI history load failed', e));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput('');
    setBusy(true);
    setMsgs((m) => [...m, { sender: 'user', content: q }, { sender: 'assistant', content: '' }]);
    try {
      await streamAiChat(q, 'admin_assistant', (delta) => {
        setMsgs((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { sender: 'assistant', content: copy[copy.length - 1].content + delta };
          return copy;
        });
      }, model);
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { sender: 'assistant', content: e.message || 'Something went wrong.' };
        return copy;
      });
    }
    setBusy(false);
  };

  const clear = async () => {
    try { await api.delete('/ai/chat/history', { params: { mode: 'admin_assistant' } }); setMsgs([]); toast.success('Conversation cleared'); }
    catch (e) { toast.error('Could not clear'); }
  };

  const runForecast = async () => {
    setPredBusy(true);
    try { const r = await api.get('/ai/predictive', { params: { days: 3 } }); setPred(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Forecast failed'); }
    setPredBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> AI Assistant</h2>
          <p className="text-sm text-muted-foreground">Business insights, predictive fuel demand and smart dispatch — powered by Gemini & Claude</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Chat */}
        <div className="xl:col-span-3 rounded-xl border border-border bg-card flex flex-col" style={{ height: '620px' }}>
          <div className="flex items-center justify-between px-4 h-12 border-b border-border shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">Ask your business anything</span>
              <div className="flex items-center rounded-full bg-muted p-0.5" data-testid="admin-ai-model-switch">
                {AI_MODELS.map((m) => (
                  <button key={m.key} onClick={() => pickModel(m.key)}
                    className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${model === m.key ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`admin-ai-model-${m.key}`}>{m.label}</button>
                ))}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={clear} data-testid="admin-ai-clear"><Trash2 className="h-4 w-4 mr-1" />Clear</Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Try one of these:</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button key={q} onClick={() => send(q)} className="text-xs rounded-full border border-border px-3 py-1.5 hover:bg-muted" data-testid={`admin-ai-quick-${QUICK_PROMPTS.indexOf(q)}`}>{q}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={`${i}-${m.sender}`} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.sender === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`} data-testid={`admin-ai-msg-${m.sender}`}>
                  {m.content || <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form className="p-3 border-t border-border flex gap-2 shrink-0" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. Which customers should we follow up with?" disabled={busy} data-testid="admin-ai-input" />
            <Button type="submit" size="icon" disabled={busy || !input.trim()} data-testid="admin-ai-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
          </form>
        </div>

        {/* Predictive panel */}
        <div className="xl:col-span-2 space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><CloudSun className="h-5 w-5 text-primary" /><span className="font-semibold text-sm">Predictive Fuel Needs</span></div>
              <Button size="sm" onClick={runForecast} disabled={predBusy} data-testid="run-forecast-button">
                {predBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}{pred ? 'Refresh' : 'Run Forecast'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Weather + demand history → truck pre-positioning for the next 3 days</p>

            {predBusy && <div className="mt-3 space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}

            {!predBusy && !pred && (
              <div className="mt-4 text-center py-6">
                <CloudSun className="h-8 w-8 mx-auto text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground mt-2">Run the forecast to see demand risk, day-by-day outlook and staging advice.</p>
              </div>
            )}

            {!predBusy && pred && (
              <div className="mt-3 space-y-3" data-testid="predictive-results">
                <div className="flex items-start gap-2">
                  <Badge className={`${riskColor(pred.risk_level)} border-0 capitalize shrink-0`}>{pred.risk_level} risk</Badge>
                  <p className="text-sm">{pred.headline}</p>
                </div>
                {(pred.day_outlook || []).map((d) => (
                  <div key={d.date} className="rounded-lg border border-border p-2.5 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">{d.date}</p>
                      <p className="text-xs text-muted-foreground">{d.driver_reco}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge variant="outline" className={`${riskColor(d.risk)} border-0 capitalize`}>{d.risk}</Badge>
                      <p className="text-xs text-muted-foreground mt-0.5">~{fmtNum(d.expected_demand_litres)} L</p>
                    </div>
                  </div>
                ))}
                {(pred.staging_recommendations || []).length > 0 && (
                  <div>
                    <SectionTitle>Truck Staging</SectionTitle>
                    {(pred.staging_recommendations || []).map((s, i) => (
                      <div key={`${s.area}-${i}`} className="flex items-start gap-2 py-1.5 text-sm">
                        <Truck className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                        <span><strong>{s.area}</strong>: {s.trucks} truck(s), stage ~{fmtNum(s.litres_to_stage)} L — {s.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(pred.actions || []).length > 0 && (
                  <div className="rounded-lg bg-muted p-3">
                    <p className="text-xs font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Recommended actions</p>
                    <ul className="text-xs space-y-1 list-disc pl-4">{(pred.actions || []).map((a) => <li key={a}>{a}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* WhatsApp bot preview (send is MOCKED until WhatsApp Business API keys are added) */}
          <div className="rounded-xl border border-border bg-card p-4" data-testid="wa-bot-preview">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" /><span className="font-semibold text-sm">WhatsApp Bot Preview</span></div>
              <Badge variant="outline" className="text-[10px]">Send mocked — awaiting API keys</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Test exactly how the AI will reply to customers on WhatsApp (welcome message, diesel-only policy, BPCL source, FAQs).</p>
            <div className="mt-3 rounded-lg bg-muted/50 border border-border p-2.5 h-56 overflow-y-auto space-y-1.5">
              {waMsgs.length === 0 && <p className="text-xs text-muted-foreground text-center pt-16">Try: "Hi", "Can I get petrol for my car?", "Where do you take fuel from?"</p>}
              {waMsgs.map((m, i) => (
                <div key={`${i}-${m.from}`} className={`flex ${m.from === 'customer' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-xs whitespace-pre-wrap ${m.from === 'customer' ? 'bg-[#d9fdd3] text-emerald-950 rounded-br-sm' : 'bg-card border border-border rounded-bl-sm'}`} data-testid={`wa-msg-${m.from}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {waBusy && <div className="flex justify-start"><div className="rounded-xl px-2.5 py-1.5 bg-card border border-border"><Loader2 className="h-3.5 w-3.5 animate-spin" /></div></div>}
              <div ref={waEndRef} />
            </div>
            <form className="mt-2 flex gap-2" onSubmit={waSend}>
              <Input value={waInput} onChange={(e) => setWaInput(e.target.value)} placeholder="Message as a WhatsApp customer..." disabled={waBusy} className="h-9 text-sm" data-testid="wa-sim-input" />
              <Button type="submit" size="icon" className="h-9 w-9 shrink-0 bg-[#25D366] hover:bg-[#1ebe5b] text-white" disabled={waBusy || !waInput.trim()} data-testid="wa-sim-send">
                {waBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
